import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { AxiosResponse } from 'axios';

import { useQueryHelpers } from '@/composables';
import { authKeys } from '@/composables/queryKeys';
import authService from '@satellite/services/auth-service';

import type { ILoginResponse, IMeResponse, IQueryArgs } from '@/types/apiTypes';

type LoginParams = { email: string; password: string };

const { useSmartQuery } = useQueryHelpers();
export default function useAuthQueries() {
  const queryClient = useQueryClient();

  const getMeQuery = (queryArgs: IQueryArgs) =>
    useSmartQuery<IMeResponse>(authKeys.me(), () => authService.getMe() as Promise<IMeResponse>, {
      enabled: queryArgs?.enabled ?? true
    });

  const loginQuery = useMutation<ILoginResponse, Error, LoginParams>({
    mutationFn: ({ email, password }) =>
      authService.login({ email, password }) as Promise<ILoginResponse>,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    }
  });

  return { getMeQuery, loginQuery };
}

type SendPasswordResetEmailParams = { email: string; recaptchaToken: string | null };

export function useSendPasswordResetEmailMutation() {
  return useMutation<AxiosResponse, Error, SendPasswordResetEmailParams>({
    mutationFn: async ({ email, recaptchaToken }) => {
      const body = recaptchaToken ? { email, recaptchaToken } : { email };
      return await axios.post('/auth/password-reset-email?client=circe', body);
    }
  });
}

type ResetPasswordParams = { token: string; email: string; password: string };

export function useResetPasswordMutation() {
  return useMutation<AxiosResponse, Error, ResetPasswordParams>({
    mutationFn: async ({ token, email, password }) =>
      await axios.post(`/auth/reset-password/${encodeURIComponent(token)}`, { email, password })
  });
}

export function useVerifyAndResetPasswordMutation() {
  return useMutation<AxiosResponse, Error, ResetPasswordParams>({
    mutationFn: async ({ token, email, password }) =>
      await axios.post(`/auth/verify-and-reset-password/${encodeURIComponent(token)}`, {
        email,
        password
      })
  });
}
